import { connect } from "react-redux";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";

import { Field, reduxForm } from "redux-form";
import { useTranslation } from "react-i18next";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormSwitch from "components/FormSwitch";
import { newAlertMessage } from "store/actions/globalAlertActions";

import TableCell from "layouts/account/components/TableCell";

function NotificationsProjectOptions({
  handleSubmit,
  pristine,
  database,
  projectId,
  projectData,
  actNewAlertMessage,
}) {
  const { t } = useTranslation();

  const submitConfig = async (data) => {
    if (!database?.editProject || !projectId) return;

    const options = data?.notificationsProjectOptions || {};

    try {
      await database.editProject({
        uid: projectId,
        notificationsProjectOptions: {
          ...(projectData?.notificationsProjectOptions || {}),
          devices: options?.devices ?? true,
          metricsLimit: options?.metricsLimit ?? true,
        },
      });

      actNewAlertMessage({
        type: "success",
        title: t("notificationsToClients"),
        content: `${t("configurationSent")}`,
      });
    } catch (error) {
      actNewAlertMessage({
        type: "error",
        title: t("notificationsToClients"),
        content: `${t("Save")} ${t("failed")}`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(submitConfig)}>
      <Card id="notifications-project-options" sx={{ overflow: "visible" }}>
        <MDBox p={3}>
          <MDTypography variant="h4">{t("notificationsToClients").toUpperCase()}</MDTypography>
        </MDBox>

        <MDBox pb={3} px={3}>
          <MDBox minWidth="auto">
            <Table sx={{ minWidth: "36rem" }}>
              <MDBox component="thead">
                <TableRow>
                  <TableCell width="100%" padding={[1.5, 3, 1.5, 0.5]}>
                    {t("Type")}
                  </TableCell>
                  <TableCell align="center" padding={[1.5, 6, 1.5, 6]}>
                    {t("enabled")}
                  </TableCell>
                </TableRow>
              </MDBox>

              <TableBody>
                <TableRow>
                  <TableCell padding={[1, 1, 1, 0.5]}>
                    <MDBox lineHeight={1.4}>
                      <MDTypography display="block" variant="button" fontWeight="regular">
                        {t("devices")}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" fontWeight="regular">
                        {t("projectNotificationsDevicesDescription")}
                      </MDTypography>
                    </MDBox>
                  </TableCell>
                  <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                    <Field name="notificationsProjectOptions.devices" component={FormSwitch} />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell padding={[1, 1, 1, 0.5]}>
                    <MDBox lineHeight={1.4}>
                      <MDTypography display="block" variant="button" fontWeight="regular">
                        {t("limits")}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" fontWeight="regular">
                        {t("projectNotificationsMetricsLimitDescription")}
                      </MDTypography>
                    </MDBox>
                  </TableCell>
                  <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                    <Field name="notificationsProjectOptions.metricsLimit" component={FormSwitch} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Grid size={{ xs: 12 }}>
              <MDBox display="flex" justifyContent="center" mt={2} xs={12}>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="dark"
                  size="small"
                  disabled={pristine}
                >
                  {t("Save")}
                </MDButton>
              </MDBox>
            </Grid>
          </MDBox>
        </MDBox>
      </Card>
    </form>
  );
}

NotificationsProjectOptions.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
  database: PropTypes.object,
  projectId: PropTypes.string,
  projectData: PropTypes.object,
};

NotificationsProjectOptions.defaultProps = {
  database: null,
  projectId: "",
  projectData: {},
};

const mapStateToProps = (state, props) => {
  const { projectsReducer = {} } = state;
  const projectData = projectsReducer[props?.projectId] || {};
  const options = projectData?.notificationsProjectOptions || {};

  return {
    projectData,
    initialValues: {
      notificationsProjectOptions: {
        devices: options?.devices ?? true,
        metricsLimit: options?.metricsLimit ?? false,
      },
    },
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "NotificationsProjectOptions",
    enableReinitialize: true,
  })(NotificationsProjectOptions)
);
